import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { notifyRolesChanged, roleNameClass } from "@/lib/liveRoles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import LiveHostControlMenu from "./LiveHostControlMenu";
import UserBadge from "@/components/UserBadge";
import VipTiersStrip from "@/components/vip/VipTiersStrip";
import VipBadge from "@/components/VipBadge";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";
import TopBadge from "@/components/TopBadge";
import LiveModActivityLog from "./LiveModActivityLog";

export default function LiveRoomViewersDrawer({
  viewers,
  onClose,
  streamId,
  isHost,
  actorRole,        // "host" | "admin" | "moderator" | "viewer" — Pair 3: Admin moderates too
}) {
  const actor = actorRole || (isHost ? "host" : "viewer");
  const canModerate = actor === "host" || actor === "admin"; // Moderators: waiting-guest list only, no in-room controls
  // Roles are session-scoped and small (max ~9 mods per stream), so a
  // simple GET on open + refresh-on-close is enough — no polling needed.
  const [roles, setRoles] = useState({}); // { [user_id]: "moderator" | "admin" }
  const [target, setTarget] = useState(null);

  const loadRoles = async () => {
    if (!streamId) return;
    try {
      const { data } = await api.get(`/live/${streamId}/roles`);
      const next = {};
      for (const r of data?.roles || []) next[r.user_id] = r.role;
      setRoles(next);
    } catch {
      /* transient — leave the previous map */
    }
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const openMenu = (p) => {
    // Host taps a viewer row → open the control menu. Non-hosts get the
    // menu too (they see the "only host can moderate" state) so profiles
    // remain clickable — matches the "every viewer is clickable" spec.
    setTarget({
      user_id: p.user_id,
      handle: p.handle,
      display_name: p.display_name || p.handle,
      avatar_url: p.avatar_url,
      role: p.is_host ? "host" : roles[p.user_id] || "viewer",
    });
  };

  return (
    <div className="absolute inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-80 max-w-[85vw] h-full bg-zinc-950/95 border-l border-white/10 p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg text-white">{viewers.length} in room</div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white"
            data-testid="viewers-drawer-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="space-y-1">
          {viewers.map((p) => {
            const badge = p.is_host
              ? { text: "Host", cls: "bg-yellow-400 text-black" }
              : roles[p.user_id] === "admin"
                ? { text: "Admin", cls: "bg-red-600 text-white" }
                : roles[p.user_id] === "moderator"
                  ? { text: "Moderator", cls: "bg-sky-500 text-white" }
                  : null;
            return (
              <div
                key={p.user_id}
                data-testid={`live-viewer-${p.user_id || p.id}`}
                className="w-full flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-white/5 transition"
              >
                {/* Avatar and name → UserBadge (opens profile preview).
                    Host-only moderation menu is reached via the separate
                    "..." button on the row, keeping the "tap a person =
                    see their profile" universal rule intact. */}
                <UserBadge
                  userRef={p.user_id || p.handle}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md"
                  aria-label={`View ${p.display_name}'s profile`}
                >
                  <Avatar className="w-8 h-8 border border-white/10">
                    <AvatarImage src={p.avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-white text-xs">
                      {p.display_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm truncate flex items-center gap-1.5 ${roleNameClass(roles[p.user_id], "text-white")}`}>{p.display_name}<LifetimeBossBadge show={p.lifetime_boss} size="xs" /><VipBadge tier={p.vip_tier} size="xs" /><TopBadge show={p.top_badge} size="xs" /></div>
                    <div className="text-[11px] text-zinc-500 truncate">{p.display_name}</div>
                  </div>
                </UserBadge>
                {badge && (
                  <span
                    data-testid={`live-role-label-${badge.text.toLowerCase()}`}
                    className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${badge.cls}`}
                  >
                    {badge.text}
                  </span>
                )}
                {canModerate && !p.is_host && !(actor === "admin" && roles[p.user_id] === "admin") && (
                  <button
                    type="button"
                    onClick={() => openMenu(p)}
                    className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white/90 rounded"
                    data-testid={`live-viewer-mod-${p.user_id || p.id}`}
                    aria-label={`Moderation actions for ${p.display_name}`}
                  >
                    <span className="text-lg leading-none">⋯</span>
                  </button>
                )}
              </div>
            );
          })}
          {viewers.length === 0 && (
            <div className="text-xs text-zinc-500 text-center pt-6">Nobody has joined yet.</div>
          )}
        </div>
        <LiveModActivityLog streamId={streamId} enabled={canModerate} />
        <VipTiersStrip context="live-viewers" className="mt-4" subtitle="Coin purchases unlock Green → Panda Boss this month." />
      </div>

      {target && (
        <LiveHostControlMenu
          streamId={streamId}
          target={target}
          isHost={isHost}
          actorRole={actor}
          onClose={() => setTarget(null)}
          onKicked={() => {
            // Refresh roles map since a grant/revoke may have happened.
            loadRoles();
            notifyRolesChanged();
          }}
        />
      )}
    </div>
  );
}
